/**
 * Admin Authors Page
 * Manage editorial team profiles (photo, bio, role) for article bylines.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'wouter';
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  Save,
  X,
  User,
  Star,
  Eye,
  EyeOff,
  Upload,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Author {
  id: string;
  name: string;
  slug: string;
  photoUrl?: string | null;
  bio?: string | null;
  role?: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface AuthorFormData {
  name: string;
  slug: string;
  photoUrl: string;
  bio: string;
  role: string;
  isDefault: boolean;
  isActive: boolean;
}

const emptyForm: AuthorFormData = {
  name: '',
  slug: '',
  photoUrl: '',
  bio: '',
  role: '',
  isDefault: false,
  isActive: true,
};

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

export default function AdminAuthors() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';

  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<AuthorFormData>(emptyForm);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [slugManual, setSlugManual] = useState(false);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      setLocation('/');
    }
  }, [authLoading, isAuthenticated, isAdmin, setLocation]);

  const { data: authors = [], isLoading } = useQuery<Author[]>({
    queryKey: ['admin-authors'],
    queryFn: async () => {
      const response = await fetch('/api/authors?all=true', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch authors');
      return response.json();
    },
    enabled: isAuthenticated && isAdmin,
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: { id?: string; data: AuthorFormData }) => {
      const body = {
        name: payload.data.name.trim(),
        slug: payload.data.slug.trim() || slugify(payload.data.name),
        photoUrl: payload.data.photoUrl.trim() || null,
        bio: payload.data.bio.trim() || null,
        role: payload.data.role.trim() || null,
        isDefault: payload.data.isDefault,
        isActive: payload.data.isActive,
      };
      const response = await fetch(payload.id ? `/api/authors/${payload.id}` : '/api/authors', {
        method: payload.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error al guardar autor');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-authors'] });
      queryClient.invalidateQueries({ queryKey: ['authors'] });
      toast({ title: 'Guardado', description: 'Perfil de autor actualizado' });
      resetForm();
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/authors/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error al eliminar');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-authors'] });
      queryClient.invalidateQueries({ queryKey: ['authors'] });
      toast({ title: 'Eliminado', description: 'Autor eliminado' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const resetForm = () => {
    setFormData(emptyForm);
    setIsCreating(false);
    setEditingId(null);
    setSlugManual(false);
  };

  const startCreate = () => {
    setFormData(emptyForm);
    setIsCreating(true);
    setEditingId(null);
    setSlugManual(false);
  };

  const startEdit = (author: Author) => {
    setFormData({
      name: author.name,
      slug: author.slug,
      photoUrl: author.photoUrl || '',
      bio: author.bio || '',
      role: author.role || '',
      isDefault: author.isDefault,
      isActive: author.isActive,
    });
    setEditingId(author.id);
    setIsCreating(false);
    setSlugManual(true);
  };

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      slug: slugManual ? prev.slug : slugify(name),
    }));
  };

  const handlePhotoUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Error', description: 'Solo se permiten imágenes', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Error', description: 'Máximo 5MB', variant: 'destructive' });
      return;
    }

    setUploadingPhoto(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const response = await fetch('/api/images/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          filename: file.name,
          mimeType: file.type,
          size: file.size,
          // Serve endpoint expects raw base64, not a data: URL
          data: dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl,
          source: 'upload',
          metadata: { width: 0, height: 0, purpose: 'author-photo' },
        }),
      });

      if (!response.ok) throw new Error('Error al subir la foto');
      const image = await response.json();
      setFormData((prev) => ({ ...prev, photoUrl: image.url || `/api/images/serve/${image.id}` }));
      toast({ title: 'Foto subida', description: 'Imagen lista para el perfil' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'No se pudo subir la foto', variant: 'destructive' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  const showForm = isCreating || editingId;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Equipo de redacción</h1>
            <p className="text-muted-foreground mt-1">
              Perfiles de autores con foto y biografía para firmar las noticias
            </p>
          </div>
          {!showForm && (
            <button
              onClick={startCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nuevo autor
            </button>
          )}
        </div>

        {showForm && (
          <div className="mb-8 border border-border rounded-xl p-6 bg-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                {editingId ? 'Editar autor' : 'Nuevo autor'}
              </h2>
              <button onClick={resetForm} className="p-1 text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-[120px_1fr]">
              <div className="flex flex-col items-center gap-2">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center border border-border">
                  {formData.photoUrl ? (
                    <img src={formData.photoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-10 h-10 text-primary" />
                  )}
                </div>
                <label className="cursor-pointer text-xs text-primary hover:underline inline-flex items-center gap-1">
                  <Upload className="w-3 h-3" />
                  {uploadingPhoto ? 'Subiendo...' : 'Subir foto'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingPhoto}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handlePhotoUpload(file);
                    }}
                  />
                </label>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombre</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background"
                    placeholder="Ana López"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => {
                      setSlugManual(true);
                      setFormData((prev) => ({ ...prev, slug: e.target.value }));
                    }}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background font-mono text-sm"
                    placeholder="ana-lopez"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Cargo / rol</label>
                  <input
                    type="text"
                    value={formData.role}
                    onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background"
                    placeholder="Redactora, Editor..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Biografía</label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData((prev) => ({ ...prev, bio: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background"
                    placeholder="Breve descripción del redactor..."
                  />
                </div>
                <div className="flex flex-wrap gap-4">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.isDefault}
                      onChange={(e) => setFormData((prev) => ({ ...prev, isDefault: e.target.checked }))}
                    />
                    Autor por defecto
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                    />
                    Activo
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={resetForm}
                className="px-4 py-2 border border-border rounded-lg hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                onClick={() => saveMutation.mutate({ id: editingId || undefined, data: formData })}
                disabled={!formData.name.trim() || saveMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg disabled:opacity-50"
              >
                {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : authors.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">No hay autores todavía</p>
        ) : (
          <div className="space-y-3">
            {authors.map((author) => (
              <div
                key={author.id}
                className="flex items-center gap-4 p-4 border border-border rounded-xl bg-card"
              >
                <div className="w-14 h-14 rounded-full overflow-hidden bg-primary/10 flex-shrink-0 flex items-center justify-center">
                  {author.photoUrl ? (
                    <img src={author.photoUrl} alt={author.name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-6 h-6 text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-foreground">{author.name}</span>
                    {author.isDefault && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                        <Star className="w-3 h-3" /> Por defecto
                      </span>
                    )}
                    {!author.isActive && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        <EyeOff className="w-3 h-3" /> Inactivo
                      </span>
                    )}
                    {author.isActive && (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Eye className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                  {author.role && (
                    <p className="text-sm text-muted-foreground">{author.role}</p>
                  )}
                  {author.bio && (
                    <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">{author.bio}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => startEdit(author)}
                    className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg"
                    title="Editar"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  {!author.isDefault && (
                    <button
                      onClick={() => {
                        if (confirm(`¿Eliminar a ${author.name}?`)) {
                          deleteMutation.mutate(author.id);
                        }
                      }}
                      className="p-2 text-muted-foreground hover:text-destructive hover:bg-muted rounded-lg"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
